/**
 * App settings — branding and business identity (FR-SET-02, FR-SET-04).
 *
 * A single `settings/app` document rather than constants in code, so that
 * `CLAUDE.md`'s "nothing is hardcoded" rule holds for the business name, the address and
 * the financial-year start month too, not just for categories.
 */

import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, getStorageLazy } from './firebase';
import { trackWrite } from './syncState';

export interface AppSettings {
  personalName: string;
  personalLogoPath: string | null;
  businessName: string;
  businessAddress: string;
  businessLogoPath: string | null;
  financialYearStartMonth: number;
}

/** Supplied 4 Aug 2026. Seeded into Firestore, editable on the Branding screen. */
export const DEFAULT_SETTINGS: AppSettings = {
  personalName: 'Vetri Vendan',
  personalLogoPath: null,
  businessName: 'Fusion Solutions',
  businessAddress:
    'Plot 66, G2, NGO Nagar, Alapakkam, New Perungalathur, Chennai 600063',
  businessLogoPath: null,
  financialYearStartMonth: 4,
};

export function watchSettings(onChange: (settings: AppSettings) => void): () => void {
  return onSnapshot(
    doc(db, 'settings', 'app'),
    (snapshot) =>
      onChange(
        snapshot.exists()
          ? { ...DEFAULT_SETTINGS, ...(snapshot.data() as Partial<AppSettings>) }
          : DEFAULT_SETTINGS,
      ),
    () => onChange(DEFAULT_SETTINGS),
  );
}

export async function saveSettings(patch: Partial<AppSettings>): Promise<void> {
  await trackWrite(setDoc(doc(db, 'settings', 'app'), patch, { merge: true }));
}

/**
 * Upload a processed logo and record its path.
 *
 * Stored under `branding/`, which the Storage rules allow any signed-in user to read —
 * unlike `loan-docs/`, which no client may read at all (AD-10). A logo is meant to be
 * displayed; the distinction is deliberate.
 */
export async function uploadLogo(
  kind: 'personal' | 'business',
  blob: Blob,
): Promise<string> {
  const { ref, uploadBytes } = await import('firebase/storage');
  const storage = await getStorageLazy();
  // Timestamped so a replacement never has to overwrite in place, which would leave
  // cached copies of the old mark in circulation.
  const path = `branding/${kind}-${Date.now()}.png`;
  await trackWrite(uploadBytes(ref(storage, path), blob, { contentType: 'image/png' }));
  await saveSettings(
    kind === 'personal' ? { personalLogoPath: path } : { businessLogoPath: path },
  );
  return path;
}

export async function logoUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  const { getDownloadURL, ref } = await import('firebase/storage');
  const storage = await getStorageLazy();
  try {
    return await getDownloadURL(ref(storage, path));
  } catch {
    return null;
  }
}
